import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'Core/utils/numberFormat';
import { getUrlParam } from 'Core/utils/utils';

const AmountSplitter = ({ decimals, form, cardCount, index }) => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    formState: { errors },
  } = form;
  const amount = watch(`cards[${index}].amount`);
  const paymentMethodId = watch(`cards[${index}].paymentMethodId`);

  if (cardCount > 1) {
    const decimalsInteger = decimals * 100;
    const hasDecimal = decimalsInteger * 100 > 0;
    let splitAmountDecimal = '.00';

    if (hasDecimal && index === 0) {
      splitAmountDecimal = decimalsInteger < 10 ? `.0${decimalsInteger}` : `.${decimalsInteger}`;
    }

    const errorMessage = errors?.cards?.[index]?.amount?.message;

    return (
      <div className={`${!paymentMethodId ? 'pt-4 border-t border-gray-200 mt-4' : 'mt-2'}`}>
        <div
          className={`
          flex items-center gap-4
          ${paymentMethodId ? 'flex-col items-end' : 'flex-col md:flex-row md:justify-between'}
        `}
        >
          <label
            htmlFor={`splitAmount-card${index}`}
            className="text-md font-semibold text-left font-medium text-gray-700 whitespace-nowrap"
          >
            {t('common.amountToPayWithCard')}
          </label>

          <div className="w-full md:w-auto">
            {/* Custom amount input with connected segments */}
            <div
              className={`
                inline-flex w-full items-stretch rounded-lg overflow-hidden border
                ${
                  errorMessage
                    ? 'border-red-400 ring-2 ring-red-100'
                    : 'border-gray-300 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
                }
              `}
            >
              <span className="flex items-center px-3 bg-gray-100 text-gray-600 font-medium border-r border-gray-300">
                $
              </span>
              <input
                id={`splitAmount-card${index}`}
                data-testid={`splitAmount-card${index}`}
                type="number"
                {...register(`cards[${index}].amount`)}
                value={amount}
                maxLength={6}
                className="
                  h-10 w-full px-3 text-right text-gray-900 font-medium
                  focus:outline-none bg-white
                  [appearance:textfield]
                  [&::-webkit-outer-spin-button]:appearance-none
                  [&::-webkit-inner-spin-button]:appearance-none
                "
              />
              <span className="flex items-center px-3 bg-gray-100 text-gray-600 font-medium border-l border-gray-300">
                {splitAmountDecimal}
              </span>
            </div>
            {errorMessage && <p className="text-sm text-red-600 mt-1">{errorMessage}</p>}
          </div>
        </div>
      </div>
    );
  }

  // Single card - display amount prominently (hidden for external user)
  const isExternalAccess =
    typeof window !== 'undefined' && getUrlParam('userAccess') === 'external';

  return (
    <div className={isExternalAccess ? 'hidden' : null}>
      <div className="flex items-center justify-between py-3 mt-3 border-t border-gray-100">
        <span className="text-md font-semibold text-gray-600">
          {!paymentMethodId && t('common.amountToPayWithCard')}
        </span>
        <span className="text-xl font-bold text-gray-900" data-testid="split-amount">
          {formatCurrency(+amount + decimals)}
        </span>
      </div>
    </div>
  );
};

export default AmountSplitter;
